import type {
    AggBankingProps,
    BankAccountAnalysis,
    AuditedStatementSlice,
    AuditedStatementsProps,
    GstExpenseProps,
    GstOverviewProps,
    GstRevenueProps,
    LinkedInDetails,
    LinkedInEntry,
} from './types';
import { CLASSIFICATION_COLUMN_KEY, DASH, MCA_SUMMARY_HOLDERS, bankAccountLabel } from '../constants';
import { AGGREGATED, ALL_ACCOUNTS, FINANCIAL_NATURE } from '../../../utils/constants/commonConstants';
import {
    CASH_FLOW_INDIRECT_TITLE,
    hasComputablePeriod,
} from '../../Dashboard/DashboardModules/AuditedFinancialsVisualisation/auditedStatementLayout';
import { hasLinkedInEnrichment } from '../../../common/ManagementProfile/managementProfileFields';
import {
    buildEntryDateRange,
    buildEntrySubline,
    formatFollowersCount,
    getEducationSubline,
    getEntryDurationMonths,
    getEntryRole,
    getWorkExperienceTotalMonths,
    pickEnriched,
} from '../../Dashboard/DashboardModules/Overview/components/ManagementProfile/components/managementUserProfileUtils';
import { formatFlexibleDateMonthYear } from '../../../utils/dateUtils';
import { normalizeNameKey } from '../../../utils/utils';
import { MONTH_ABBR, shortMonthLabel } from './monthLabels';
import {
    BOUNCE_CHARGES_HEADER,
    BOUNCE_TRANSACTIONS_HEADER,
    CASH_AND_CHEQUE_HEADER,
    CREDITS_AND_DEBITS_HEADER,
    formatBounceCellText,
    OTHER_TRANSACTION_FAILURES_HEADER,
    PAYMENT_METHOD_HEADER,
    splitRowsBySection,
} from '../../Dashboard/DashboardModules/BankDataTable/bankSummarySections';

/**
 * Sorts an array of month header strings into ascending chronological order.
 * Handles "MM-YYYY" (e.g. "04-2024"), "Mon_YYYY" / "Mon-YYYY" (e.g. "Oct_2023"),
 * and other formats by delegating to shortMonthLabel for normalisation then
 * parsing the resulting "Mmm'YY" into a YYYYMM integer for comparison.
 * Returns a new array; does not mutate the input.
 */
export function sortMonthHeadersChrono(headers: string[]): string[] {
    const toYYYYMM = (s: string): number => {
        // Try "MM-YYYY" directly
        const mmYYYY = /^(\d{1,2})[-_](\d{4})$/.exec(s.trim());
        if (mmYYYY) return parseInt(mmYYYY[2], 10) * 100 + parseInt(mmYYYY[1], 10);
        // Normalise via shortMonthLabel → "Mmm'YY"
        const norm = shortMonthLabel(s);
        const mmmYY = /^([A-Za-z]{3})'(\d{2})$/.exec(norm.trim());
        if (mmmYY) {
            const monthIdx = MONTH_ABBR.findIndex((m) => m.toLowerCase() === mmmYY[1].toLowerCase());
            if (monthIdx !== -1) {
                const yy = parseInt(mmmYY[2], 10);
                const yyyy = yy >= 50 ? 1900 + yy : 2000 + yy;
                return yyyy * 100 + (monthIdx + 1);
            }
        }
        // Unparseable — treat as 0 so it sorts before everything
        return 0;
    };
    return [...headers].sort((a, b) => toYYYYMM(a) - toYYYYMM(b));
}

export function normalizeInsightsBlock(raw: any): any {
    const d = raw?.data;
    if (!d) return undefined;
    const observations = Array.isArray(d.observations) ? d.observations.filter(Boolean) : [];
    const recommendations = Array.isArray(d.recommendations) ? d.recommendations.filter(Boolean) : [];
    const risks = Array.isArray(d.risks) ? d.risks.filter(Boolean) : [];
    if (observations.length === 0 && recommendations.length === 0 && risks.length === 0) return undefined;
    return {
        moduleName: d.moduleName || d.moduleId || '',
        observations,
        recommendations,
        risks,
        createdAt: d.createdAt,
    };
}

export function normalizeInsights(parts: {
    overview?: any;
    compliance?: any;
    legalChecks?: any;
    auditedFinancials?: any;
    relatedParty?: any;
}): any {
    const overview = normalizeInsightsBlock(parts.overview);
    const compliance = normalizeInsightsBlock(parts.compliance);
    const legalChecks = normalizeInsightsBlock(parts.legalChecks);
    const auditedFinancials = normalizeInsightsBlock(parts.auditedFinancials);
    const relatedParty = normalizeInsightsBlock(parts.relatedParty);
    if (!overview && !compliance && !legalChecks && !auditedFinancials && !relatedParty) return undefined;
    return { overview, compliance, legalChecks, auditedFinancials, relatedParty };
}

function normalizeStatementSlice(name: string, raw: any) {
    const d = raw?.data?.data ?? raw?.data ?? raw;
    if (!d || !Array.isArray(d.headers) || !Array.isArray(d.rows)) return undefined;
    const headers = d.headers.map((h: any) => ({ name: h.name, key: h.key, periodStatus: h.periodStatus, statusReason: h.statusReason }));
    const sparkYears = headers.filter((h: any) => /^\d{4}$/.test(h.key)).map((h: any) => h.key);
    const rows = d.rows
        .filter((r: any) => r.classificationName && r.classificationName !== 'Reference Document')
        .map((r: any) => {
            const values: Record<string, string> = {};
            headers.forEach((h: any) => {
                if (h.key === 'classificationName') return;
                if (r[h.key] != null) values[h.key] = String(r[h.key]);
            });
            return {
                classificationName: r.classificationName,
                label: r.label,
                subLabel: r.subLabel,
                isAggregated: !!r.isAggregated,
                values,
            };
        });
    if (rows.length === 0) return undefined;
    return { name, headers, rows, sparkYears };
}

const hasValue = (v: string) => v.trim() !== '' && v.trim() !== DASH;

const sliceHasValue = (slice: AuditedStatementSlice): boolean => slice.rows.some((r) => Object.values(r.values).some(hasValue));

export const renderableStatements = (statements: AuditedStatementSlice[]): AuditedStatementSlice[] =>
    statements.filter(
        (s) => s.name !== CASH_FLOW_INDIRECT_TITLE || hasComputablePeriod(s.headers.filter((h) => h.key !== CLASSIFICATION_COLUMN_KEY)),
    );

const normalizeSlices = (raw: Array<{ name?: string; payload: any }> | undefined): AuditedStatementSlice[] =>
    (raw ?? []).flatMap((s) => normalizeStatementSlice(s.name ?? 'Statement', s.payload) ?? []);

const hasVersionContent = (slices: AuditedStatementSlice[]) => renderableStatements(slices).some(sliceHasValue);

export function normalizeAuditedStatements(raw: any): AuditedStatementsProps | undefined {
    if (!raw) return undefined;
    const standalone = normalizeSlices(raw.byNature?.[FINANCIAL_NATURE.STANDALONE]);
    const consolidated = normalizeSlices(raw.byNature?.[FINANCIAL_NATURE.CONSOLIDATED]);
    const unlabelled = normalizeSlices(raw.slices);
    if (standalone.length + consolidated.length + unlabelled.length === 0) return undefined;
    return {
        standalone,
        consolidated,
        unlabelled,
        hasStandalone: hasVersionContent(standalone),
        hasConsolidated: hasVersionContent(consolidated),
    };
}

// Provisional statements — same slice shape as audited; consumed inside the
// Audited module as a fallback when audited slices aren't available.
export function normalizeProvisionalStatements(raw: any): AuditedStatementsProps | undefined {
    if (!raw || !Array.isArray(raw.slices)) return undefined;
    const unlabelled = normalizeSlices(raw.slices);
    if (unlabelled.length === 0) return undefined;
    return { unlabelled, hasStandalone: false, hasConsolidated: false };
}

const realBankingAccounts = (list: any[]): any[] => list.filter((a) => a?.name !== ALL_ACCOUNTS);

// Banking Overview — per-account snapshot (closing balance, OD limit, LTM
// transactions, missing-days). Source: Polus `data/bankStatementData` returns
// `{ bankData: { bankingOverview: [...], summaryData: [...] } }`. We accept
// either the unwrapped `bankData`, the response root, or the array directly.
export function normalizeBankingOverview(raw: any): any {
    const list = Array.isArray(raw?.bankingOverview)
        ? raw.bankingOverview
        : Array.isArray(raw?.bankData?.bankingOverview)
        ? raw.bankData.bankingOverview
        : Array.isArray(raw)
        ? raw
        : undefined;
    if (!list || list.length === 0) return undefined;
    const accounts = realBankingAccounts(list).map((a: any) => ({
        accountId: a?.accountId,
        name: a?.name,
        accountNumber: a?.accountNumber,
        closingBalance: typeof a?.closingBalance === 'number' ? a.closingBalance : undefined,
        closingBalanceDate: a?.closingBalanceDate ?? null,
        odSanctionLimit: typeof a?.odSanctionLimit === 'number' ? a.odSanctionLimit : undefined,
        transactionsLTM: typeof a?.transactionsLTM === 'number' ? a.transactionsLTM : undefined,
        missingDays: typeof a?.missingDays === 'number' ? a.missingDays : undefined,
    }));
    return accounts.length ? { accounts } : undefined;
}

const parseRupees = (v: string): number => {
    const cleaned = String(v ?? '')
        .replace(/[₹,\s]/g, '')
        .trim();
    if (!cleaned) return 0;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
};

const cleanCharge = (v: any): string | undefined => {
    if (v == null) return undefined;
    const s = String(v).trim();
    if (!s || s === '-' || s === '—') return undefined;
    return s;
};

export function normalizeMcaCharges(raw: any): any {
    const rowData = raw?.graphs?.mcaCharges?.plots?.[0]?.rowData;
    if (!Array.isArray(rowData)) return undefined;
    const chargeEntries = rowData.filter(
        (entry: any) => !(entry.amounts ?? []).some((a: any) => a.xValue === 'Charge Holder Name' && MCA_SUMMARY_HOLDERS.has(a.rowVal)),
    );
    if (chargeEntries.length === 0) return undefined;
    const rows = chargeEntries.map((entry: any) => {
        const map = new Map<string, string>();
        (entry.amounts ?? []).forEach((a: any) => map.set(a.xValue, a.rowVal));
        const amount = map.get('Charge Amount') ?? '';
        return {
            holder: map.get('Charge Holder Name') ?? '—',
            amount,
            amountNumeric: parseRupees(amount),
            creationDate: map.get('Creation Date') ?? '—',
            modificationDate: cleanCharge(map.get('Modification Date')),
            status: (map.get('Status') ?? '—').toUpperCase(),
            propertyType: map.get('Property Type') ?? '—',
            instrumentDesc: cleanCharge(map.get('Instrument Description')),
            rateOfInterest: cleanCharge(map.get('Rate of Interest')),
            termsOfPayment: cleanCharge(map.get('Terms of Payment')),
            numberOfHolders: cleanCharge(map.get('Number of Holders')),
            propertyParticulars: cleanCharge(map.get('Property Particulars')),
            extentAndOperation: cleanCharge(map.get('Extent and Operation')),
            otherTerms: cleanCharge(map.get('Other Terms')),
        };
    });
    const open = rows.filter((r: any) => r.status === 'OPEN');
    const closed = rows.length - open.length;
    const totalOpenNumeric = open.reduce((s: number, r: any) => s + r.amountNumeric, 0);
    const totalOpenAmountLabel =
        totalOpenNumeric >= 1e7
            ? `₹${(totalOpenNumeric / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`
            : `₹${totalOpenNumeric.toLocaleString('en-IN')}`;
    return { rows, totalOpen: open.length, totalOpenAmountLabel, totalClosed: closed };
}

const DD_PLACEHOLDER =
    /^(\s*(n\/?a|na|none|nil|null|undefined|tbd|—|-+)\s*|\s*data\s+(not\s+(found|available)|unavailable)\s*|\s*not\s+available\s*|\s*no\s+data\s*(available)?\s*)$/i;
const cleanField = (v: any): string | undefined => {
    if (v == null) return undefined;
    const s = String(v).trim();
    if (!s || DD_PLACEHOLDER.test(s)) return undefined;
    return s;
};
const cleanScore = (v: any): number | undefined => {
    if (v == null) return undefined;
    const n = typeof v === 'number' ? v : Number(String(v).replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return n;
};
// Try every field-name variant we've seen in the wild — different upstream
// services (Karza, MCA, internal) use different keys for the same concepts.
// Without this fallback chain, beginDate/endDate quietly came up empty for
// directors whose source happens to call them startDate/cessationDate.
const cleanEntities = (list: any) =>
    (Array.isArray(list) ? list : [])
        .map((e: any) => ({
            name: cleanField(e?.name) ?? cleanField(e?.entityName) ?? cleanField(e?.companyName) ?? '',
            role: cleanField(e?.role) ?? cleanField(e?.designation),
            beginDate: cleanField(e?.beginDate) ?? cleanField(e?.startDate) ?? cleanField(e?.appointedOn),
            endDate: cleanField(e?.endDate) ?? cleanField(e?.endingDate) ?? cleanField(e?.cessationDate) ?? cleanField(e?.resignedOn),
        }))
        .filter((e: any) => e.name);

export function normalizeLinkedIn(profile: any): LinkedInDetails | undefined {
    const cf = profile?.customFields;
    if (!hasLinkedInEnrichment(cf)) return undefined;
    const work: any[] = pickEnriched<any>(cf.workExperience, profile.workExperience) ?? [];
    const education: any[] = pickEnriched<any>(cf.education, profile.education) ?? [];
    return {
        headline: cleanField(cf.headline) ?? cleanField(profile.subTitle),
        followers: formatFollowersCount(cf.followersCount ?? profile.followersCount),
        url: cleanField(cf.linkedinUrl) ?? cleanField(profile.linkedinUrl),
        imageUrl: cleanField(cf.imageUrl) ?? cleanField(profile.imageUrl),
        totalExperienceMonths: getWorkExperienceTotalMonths(work) ?? undefined,
        workExperience: work
            .map((w: any, i: number) => ({
                title: cleanField(w?.companyName) ?? '',
                subtitle: buildEntrySubline(getEntryRole(w), getEntryDurationMonths(w, i)),
                logo: cleanField(w?.logo),
                dateRange: buildEntryDateRange(
                    formatFlexibleDateMonthYear(w?.startDate),
                    w?.endDate ? formatFlexibleDateMonthYear(w.endDate) : null,
                    i === 0,
                ),
            }))
            .filter((entry: LinkedInEntry) => entry.title),
        education: education
            .map((ed: any, i: number) => ({
                title: cleanField(ed?.schoolName) ?? '',
                subtitle: buildEntrySubline(getEducationSubline(ed), getEntryDurationMonths(ed, i)),
                logo: cleanField(ed?.logo),
                dateRange: buildEntryDateRange(formatFlexibleDateMonthYear(ed?.startDate), formatFlexibleDateMonthYear(ed?.endDate)),
            }))
            .filter((entry: LinkedInEntry) => entry.title),
    };
}

export function mergeLinkedIn(primary?: LinkedInDetails, fallback?: LinkedInDetails): LinkedInDetails | undefined {
    if (!primary || !fallback) return primary ?? fallback;
    return {
        headline: primary.headline ?? fallback.headline,
        followers: primary.followers ?? fallback.followers,
        url: primary.url ?? fallback.url,
        imageUrl: primary.imageUrl ?? fallback.imageUrl,
        totalExperienceMonths: primary.totalExperienceMonths ?? fallback.totalExperienceMonths,
        workExperience: primary.workExperience.length > 0 ? primary.workExperience : fallback.workExperience,
        education: primary.education.length > 0 ? primary.education : fallback.education,
    };
}

type DirectorsDeepInput = any[] | { overviewList?: any[]; detailsList?: any[] } | undefined;

export function normalizeDirectorsDeep(input: DirectorsDeepInput): any {
    if (!input) return undefined;
    const overviewList: any[] = Array.isArray(input) ? [] : Array.isArray(input.overviewList) ? input.overviewList : [];
    const detailsList: any[] = Array.isArray(input) ? input : Array.isArray(input.detailsList) ? input.detailsList : [];

    const detailsByName = new Map<string, any>();
    const detailsByDin = new Map<string, any>();
    detailsList.forEach((raw) => {
        const p = raw?.responseBody?.managementProfile?.profile;
        if (!p) return;
        const key = (cleanField(p.name) ?? '').toLowerCase().trim();
        if (key) detailsByName.set(key, p);
        const din = normalizeNameKey(p.din);
        if (din) detailsByDin.set(din, p);
    });

    // Pick a primary list to drive the order: overview if available, else details (back-compat shape).
    const primary: any[] =
        overviewList.length > 0 ? overviewList : detailsList.map((raw) => raw?.responseBody?.managementProfile?.profile).filter(Boolean);

    // Dedup by directorId or name — overview list can repeat.
    const seen = new Set<string>();
    const profiles: any[] = [];
    primary.forEach((row: any, index: number) => {
        const overview = overviewList.length > 0 ? row : null;
        const name = cleanField(row?.name) ?? cleanField(overview?.name) ?? '—';
        const key = (cleanField(row?.directorId) ?? name).toLowerCase().trim();
        if (key && seen.has(key)) return;
        seen.add(key);

        const rowDin = normalizeNameKey(row?.din);
        const indexedDetail = overviewList.length > 0 ? detailsList[index]?.responseBody?.managementProfile?.profile : undefined;
        const indexedDin = normalizeNameKey(indexedDetail?.din);
        // The detail at the same index is only trusted when its DIN does not contradict the row's.
        const seedDetail = !rowDin || !indexedDin || indexedDin === rowDin ? indexedDetail : undefined;
        const det = (rowDin ? detailsByDin.get(rowDin) : undefined) ?? seedDetail ?? detailsByName.get(name.toLowerCase().trim());
        const score = cleanScore(overview?.cibilScore ?? row?.cibilScore ?? det?.creditScore ?? row?.creditScore);
        const bureau = cleanField(overview?.creditBureau) ?? cleanField(det?.creditBureau) ?? cleanField(row?.creditBureau);
        const dpdAccounts = Number.isFinite(overview?.dpdAccounts) ? overview.dpdAccounts : undefined;
        const totalAccounts = Number.isFinite(overview?.totalAccounts) ? overview.totalAccounts : undefined;
        const currentOverdue = Number.isFinite(overview?.currentOverdue) ? overview.currentOverdue : undefined;
        const maxDpds = Number.isFinite(overview?.maxDpds) ? overview.maxDpds : undefined;
        const enquiriesInPast90Days = Number.isFinite(overview?.enquiriesInPast90Days) ? overview.enquiriesInPast90Days : undefined;

        // Bureau staleness + failure state. lastFetched is epoch-ms; bureauError
        // is a string (or null) sent through cleanField so empty strings drop.
        const lastFetched = Number.isFinite(overview?.lastFetched) ? overview.lastFetched : undefined;
        const bureauError = cleanField(overview?.bureauError);

        profiles.push({
            name,
            din: cleanField(row?.din) ?? cleanField(det?.din),
            designation: cleanField(det?.designation) ?? cleanField(row?.designation) ?? 'Director',
            phone: cleanField(det?.phone),
            pan: cleanField(det?.pan) ?? cleanField(det?.maskedPan) ?? cleanField(row?.primaryPan),
            gender: cleanField(det?.gender),
            creditScore: score,
            creditBureau: bureau,
            legalCasesCount: Number.isFinite(det?.legalCasesCount) ? det.legalCasesCount : 0,
            currentEntities: cleanEntities(det?.currentEntities),
            formerEntities: cleanEntities(det?.formerEntities),
            dpdAccounts,
            totalAccounts,
            currentOverdue,
            maxDpds,
            enquiriesInPast90Days,
            lastFetched,
            bureauError,
            linkedin: mergeLinkedIn(normalizeLinkedIn(det), normalizeLinkedIn(row)),
        });
    });

    return profiles.length ? { profiles } : undefined;
}

// Map an upstream `creditBureau` string ("CIBIL" / "EQUIFAX" / "EXPERIAN" / etc.)
// to the three canonical bureau labels used by the Figma. Anything else falls
// back to CIBIL (the most common upstream default).
const toBureauLabel = (raw: any): 'CIBIL' | 'Equifax' | 'Experian' => {
    const up = String(raw ?? '').toUpperCase();
    if (up.includes('EQUIFAX')) return 'Equifax';
    if (up.includes('EXPERIAN')) return 'Experian';
    return 'CIBIL';
};

const daysAgoFromTimestamp = (ts: any): number | undefined => {
    if (ts == null) return undefined;
    const t = typeof ts === 'number' ? ts : Date.parse(String(ts));
    if (!Number.isFinite(t)) return undefined;
    const diff = Date.now() - t;
    if (diff < 0) return 0;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
};

// Figma 171:4058 expects three bureau rows (CIBIL / Equifax / Experian) per
// director. The upstream `combinedDirectors` API surfaces one bureau row per
// director (the bureau that was actually fetched). We render whichever rows
// the API gives us — never fabricate the missing two.
// TODO: Backend needs per-bureau breakdown to render all 3 rows per Figma 171:4058.
export function normalizeDirectorBureau(input: any): any {
    if (!input) return undefined;
    const overviewList: any[] = Array.isArray(input) ? [] : Array.isArray(input.overviewList) ? input.overviewList : [];
    if (overviewList.length === 0) return undefined;

    const grouped = new Map<string, { name: string; rows: any[] }>();
    overviewList.forEach((row: any) => {
        const name = cleanField(row?.name);
        if (!name) return;
        const score = cleanScore(row?.cibilScore);
        const dpdAccounts = Number.isFinite(row?.dpdAccounts) ? row.dpdAccounts : undefined;
        const currentOverdue = Number.isFinite(row?.currentOverdue) ? row.currentOverdue : undefined;
        const maxDpds = Number.isFinite(row?.maxDpds) ? row.maxDpds : undefined;
        const fetchedDaysAgo = daysAgoFromTimestamp(row?.lastFetched);
        // Skip rows where every numeric field is missing — nothing to render.
        if (score == null && dpdAccounts == null && currentOverdue == null && maxDpds == null) return;

        const key = name.toLowerCase().trim();
        if (!grouped.has(key)) grouped.set(key, { name, rows: [] });
        const bureau = toBureauLabel(row?.creditBureau);
        // Dedup same-bureau within a director (overviewList can repeat).
        const existing = grouped.get(key)!;
        if (existing.rows.some((r) => r.bureau === bureau)) return;
        existing.rows.push({
            bureau,
            score,
            dpdAccounts,
            currentOverdue,
            maxDpds,
            fetchedDaysAgo,
        });
    });

    const directors = Array.from(grouped.values()).filter((d) => d.rows.length > 0);
    return directors.length ? { directors } : undefined;
}

export function normalizeFinancialMetrics(raw: any): any {
    const body = raw?.responseBody;
    const metrics = body?.UW_FETCH_FINANCIAL_METRICS?.data?.data;
    if (!metrics || !Array.isArray(metrics.headers) || !Array.isArray(metrics.rows)) return undefined;
    const headers = metrics.headers
        .filter((h: any) => h.key && h.key !== 'classificationName')
        .map((h: any) => ({ name: h.name ?? '', key: h.key }));
    if (headers.length === 0) return undefined;
    const rows = metrics.rows
        .filter((r: any) => r.metricName)
        .map((r: any) => {
            const values: Record<string, number | string | null> = {};
            headers.forEach((h: any) => {
                values[h.key] = r[h.key] ?? null;
            });
            return { metricName: r.metricName, unit: r.unit, values };
        });
    if (rows.length === 0) return undefined;
    return { headers: [{ name: 'Metric', key: 'metricName' }, ...headers], rows };
}

// ═══════════════════════════════════════════════════════════════
// GST Revenue normalizer
// Raw shape (from fetchGstr3bDataService): { UW_GSTR3B_DATA: { data: { headers, metricsData } }, AION_GET_GST_LIST: {...} }
// Raw shape (from fetchRevenueVendorExpenseTableDataService): { headers: [{name,key}], rows: [...], totalRecords }
// ═══════════════════════════════════════════════════════════════

// ── Helper: parse Indian-formatted number string to numeric value ─
const parseIndianNum = (s: string | number | null | undefined): number => {
    if (s == null) return 0;
    if (typeof s === 'number') return s;
    const cleaned = String(s).replace(/[,\s₹]/g, '');
    if (cleaned === '' || cleaned === '—' || cleaned === '-') return 0;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
};

// ── Helper: format "MM-YYYY" → "MMM'YY" display label ───────────
const fmtMonthLabel = (raw: string): string => {
    // Handle "MM-YYYY" format (e.g. "10-2023" → "Oct'23")
    const mmyyyy = raw.match(/^(\d{1,2})-(\d{4})$/);
    if (mmyyyy) {
        const monthIdx = parseInt(mmyyyy[1], 10) - 1;
        const yr = mmyyyy[2].slice(-2);
        return `${MONTH_ABBR[monthIdx] ?? raw}'${yr}`;
    }
    // Already formatted or unknown — return as-is
    return raw;
};

// ── Helpers: extract chart points from server graph response ─────
const getGraph = (chartsRaw: any, name: string): any => {
    const graphs = chartsRaw?.data?.graphs ?? chartsRaw?.graphs ?? chartsRaw ?? {};
    return graphs[name];
};

// plotValues use: xValue (label), yValue/yValue2/yValue3 (values)
// Pie charts use: name, value
// metadata.legend maps yValue/yValue2 → display label
// metadata.value → headline stat value
// metadata.title → chart title

const readVal = (v: any): number => {
    const raw = v.yValue ?? v.y ?? v.value;
    return typeof raw === 'number' ? raw : parseFloat(String(raw ?? '0')) || 0;
};

const readLabel = (v: any): string => v.xValue ?? v.name ?? v.label ?? v.x ?? '';

const extractLinePoints = (graph: any): Array<{ label: string; value: number }> => {
    if (!graph?.plots?.length) return [];
    const values = graph.plots[0].plotValues ?? [];
    return values
        .map((v: any) => {
            const value = readVal(v);
            return { label: readLabel(v), value };
        })
        .filter((p: any) => p.label);
};

// Chart-API yValues are already in ₹ Lacs — format them as Lacs/Cr directly.
// (fmtLacCompact assumes raw rupees and would render "₹250" for 250 Lacs.)
const fmtChartLac = (v: number): string => (Math.abs(v) >= 100 ? `₹${(v / 100).toFixed(2)} Cr` : `₹${v.toFixed(2)} L`);

// NOT every graph uses Lacs: bar graphs like `domesticRevenueAcrossStates`
// carry raw-rupee yValues (the state totals sum to the company's total sales
// only when read as rupees). Callers pass `unit: 'rupees'` for those — the
// label then shows the exact rupee figure in Indian comma format (₹81,37,003)
// instead of a Lacs/Cr approximation.
const extractBarItems = (
    graph: any,
    unit: 'lacs' | 'rupees' = 'lacs',
): Array<{ label: string; value: string; percent: number; raw: number }> => {
    if (!graph?.plots?.length) return [];
    const values = graph.plots[0].plotValues ?? [];
    const nums = values.map((v: any) => readVal(v));
    const maxVal = Math.max(...nums.filter(Number.isFinite), 1);
    return values
        .map((v: any) => {
            const numVal = readVal(v);
            const label = readLabel(v);
            if (!label) return null;
            const pct = Math.round((numVal / maxVal) * 100);
            const fallbackLabel = unit === 'rupees' ? `₹${Math.round(numVal).toLocaleString('en-IN')}` : fmtChartLac(numVal);
            return { label, value: v.displayValue ?? fallbackLabel, percent: pct, raw: numVal };
        })
        .filter(Boolean);
};

const extractMultiLine = (graph: any): Array<{ label: string; color: string; points: Array<{ label: string; value: number }> }> => {
    if (!graph?.plots?.length) return [];
    const plot0 = graph.plots[0];
    const values = plot0.plotValues ?? [];
    const legend: Record<string, string> = graph.metadata?.legend ?? {};
    const FALLBACK_COLORS = ['#6366F1', '#2D8B75', '#F59E0B', '#06B6D4', '#1a1a1a'];
    const colorData = graph.colors ?? {};

    // Multi-series: yValue, yValue2, yValue3 are different series in same plotValues array
    const seriesKeys = ['yValue', 'yValue2', 'yValue3', 'yValue4'].filter((k) => values.some((v: any) => v[k] != null));
    if (seriesKeys.length === 0) return [];

    const colorMap: Record<string, string> = {
        yValue: colorData.primary ?? FALLBACK_COLORS[0],
        yValue2: colorData.secondary ?? FALLBACK_COLORS[1],
        yValue3: colorData.tertiary ?? FALLBACK_COLORS[2],
        yValue4: colorData.fourth ?? FALLBACK_COLORS[3],
    };

    return seriesKeys.map((key, i) => ({
        label: legend[key] ?? `Series ${i + 1}`,
        color: colorMap[key] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
        points: values
            .map((v: any) => {
                const val = typeof v[key] === 'number' ? v[key] : parseFloat(String(v[key] ?? '0')) || 0;
                return { label: readLabel(v), value: val };
            })
            .filter((p: any) => p.label),
    }));
};

const extractStackedBars = (graph: any): Array<{ label: string; recurring: number; nonRecurring: number }> => {
    if (!graph?.plots?.length) return [];
    const values = graph.plots[0].plotValues ?? [];
    // Stacked bars: yValue = first stack, yValue2 = second stack
    if (!values.some((v: any) => v.yValue != null || v.yValue2 != null)) return [];
    return values.map((v: any) => ({
        label: readLabel(v),
        recurring: typeof v.yValue === 'number' ? v.yValue : parseFloat(v.yValue ?? '0') || 0,
        nonRecurring: typeof v.yValue2 === 'number' ? v.yValue2 : parseFloat(v.yValue2 ?? '0') || 0,
    }));
};

const extractHsnCategories = (graph: any): Array<{ name: string; percent: number; color: string }> | undefined => {
    if (!graph?.plots?.length) return undefined;
    const values = graph.plots[0].plotValues ?? [];
    const HSN_COLORS = ['#1a6b58', '#2D8B75', '#6BC4A6', '#B8E4D4', '#E8E8E8'];
    if (values.length === 0) return undefined;
    // Pie chart uses { name, value }
    return values.map((v: any, i: number) => ({
        name: v.name ?? readLabel(v),
        percent: typeof v.value === 'number' ? Math.round(v.value) : Math.round(readVal(v)),
        color: v.color ?? v.fill ?? HSN_COLORS[i % HSN_COLORS.length],
    }));
};

const extractConcentration = (graph: any): { majorLabel: string; majorPct: number; minorLabel: string; minorPct: number } | undefined => {
    // The concentration graph's plotValues only carry a label (xValue) and a
    // percentage (yValue). The counts/amounts the old extraction guessed at
    // (count, displayValue, customerPct) are not sent by this API and
    // rendered as zeros / "₹80"-style garbage — so the card now shows only
    // the real percentage split. The headline comes from graph metadata
    // (chartMeta) in the renderer.
    const values = graph?.plots?.[0]?.plotValues ?? [];
    if (values.length < 2) return undefined;
    const sorted = [...values].sort((a: any, b: any) => readVal(b) - readVal(a));
    // The API labels these slices "Section 1"/"Section 2" — meaningless to a
    // reader, so drop them and let the card fall back to its descriptive copy
    // ("Top customers" / "Other customers").
    const cleanLabel = (s: string): string => (/^section\s*\d+$/i.test(String(s).trim()) ? '' : String(s));
    return {
        majorLabel: cleanLabel(readLabel(sorted[0])),
        majorPct: Math.round(readVal(sorted[0])),
        minorLabel: cleanLabel(readLabel(sorted[1])),
        minorPct: Math.round(readVal(sorted[1])),
    };
};

// ── Shared GSTR-3B parsing ───────────────────────────────────────
// The GSTR-3B payload carries all four categories (Revenue / Tax Liability /
// Purchases / ITC Claimed) in one response; the revenue and expense modules
// each keep a different subset. `keepCategories` selects which categories'
// rows are emitted — include '' to also keep rows that appear before any
// category header (the revenue table does, expenses doesn't).
const GSTR3B_CATEGORIES = new Set(['Revenue', 'Tax Liability', 'Purchases', 'ITC Claimed']);
const GSTR3B_NON_MONTH_TYPES = new Set(['label', 'classification', 'total', 'header']);

const unwrapGstr3bData = (raw: any): any =>
    raw?.responseBody?.UW_GSTR3B_DATA?.data ?? raw?.data?.UW_GSTR3B_DATA?.data ?? raw?.UW_GSTR3B_DATA?.data ?? raw?.data ?? {};

type Gstr3bParsed = {
    data: any;
    monthHeaders: Array<{ key: string; label: string }>;
    rows: Array<{ classification: string; category?: string; bold?: boolean; amounts?: Record<string, string> }>;
};

const parseGstr3b = (raw: any, keepCategories: Set<string>): Gstr3bParsed => {
    const data = unwrapGstr3bData(raw);
    // Month columns: skip the classification/label/total columns, sort
    // chronologically, cap to the last 12, key by raw header value with a
    // display-formatted label.
    const filteredHeaders = ((data?.headers ?? []) as Array<{ value: string; type: string }>).filter((h) =>
        !h.type || h.type === '' ? true : !GSTR3B_NON_MONTH_TYPES.has(h.type),
    );
    const sortedValues = sortMonthHeadersChrono(filteredHeaders.map((h) => h.value));
    const monthHeaders = sortedValues.map((v) => ({ key: v, label: fmtMonthLabel(v) })).slice(-12);

    const rows: Gstr3bParsed['rows'] = [];
    let currentCategory = '';
    for (const entry of Object.values(data?.metricsData ?? {}) as any[]) {
        const key = entry?.key ?? '';
        const label = entry?.value ?? key;
        if (GSTR3B_CATEGORIES.has(key) && key !== currentCategory) {
            currentCategory = key;
            if (keepCategories.has(key)) rows.push({ classification: key, category: key });
        }
        if (!keepCategories.has(currentCategory)) continue;
        // Build amounts keyed by display label (the renderer looks them up by label).
        const amounts: Record<string, string> = {};
        const rawAmounts = entry?.amounts ?? {};
        for (const mh of monthHeaders) {
            const val = rawAmounts[mh.key];
            amounts[mh.label] = val != null ? String(val) : '—';
        }
        if (Object.values(amounts).some((v) => v !== '—')) {
            rows.push({ classification: label, bold: entry?.bold ?? false, amounts });
        }
    }
    return { data, monthHeaders, rows };
};

// Headline text from a server graph's metadata — the title / primaryText /
// secondaryText fields the dashboard Graph cards render. Shared by both GST
// normalizers.
const pickMeta = (g: any): { title?: string; primaryText?: string; secondaryText?: string } | undefined =>
    g?.metadata ? { title: g.metadata.title, primaryText: g.metadata.primaryText, secondaryText: g.metadata.secondaryText } : undefined;

export function normalizeGstOverview(gstOverviewRaw?: any): GstOverviewProps | undefined {
    // Primary shape: { responseBody: { summary, rows } } (Polus/AICA envelope).
    // Fallbacks cover: { data: { responseBody } } (nested Polus), { data: { data } }
    // and { data } (Aion direct), and bare object (report builder passthrough).
    const data =
        gstOverviewRaw?.data?.responseBody ??
        gstOverviewRaw?.responseBody ??
        gstOverviewRaw?.data?.data ??
        gstOverviewRaw?.data ??
        gstOverviewRaw ??
        {};
    const summary = data?.summary;
    const rows: any[] = data?.rows ?? [];
    if (!summary && rows.length === 0) return undefined;

    const totalGstinCount = summary?.totalGstinCount ?? 0;
    const activeGstinCount = summary?.activeGstinCount ?? 0;
    if (totalGstinCount === 0 && rows.length === 0) return undefined;

    const perState = summary?.stateCoverage?.perState ?? [];
    const stateCoverage = perState.map((s: any) => ({
        stateName: s.stateName ?? '',
        gstinCount: s.gstinCount ?? 0,
        activeCount: s.activeCount ?? 0,
        inactiveCount: s.inactiveCount ?? 0,
    }));

    const gstinRows = rows.map((r: any) => ({
        gstin: r.gstin ?? '',
        status: r.status ?? '',
        state: r.state ?? '',
        tradeName: r.tradeName ?? null,
        natureOfBusiness: Array.isArray(r.natureOfBusiness) ? r.natureOfBusiness : [],
        hsn: Array.isArray(r.hsn) ? r.hsn.map((h: any) => ({ code: h.code ?? '', description: h.description ?? '' })) : [],
        sac: Array.isArray(r.sac) ? r.sac.map((s: any) => ({ code: s.code ?? '', description: s.description ?? '' })) : [],
        address: r.address ?? '',
    }));

    return {
        totalGstinCount,
        activeGstinCount,
        hsnSacActiveCount: summary?.hsnSacActiveCount ?? 0,
        latestFy: summary?.latestFy ?? '',
        latestFyTurnoverBand: summary?.latestFyTurnoverBand ?? '',
        yoYTurnover: (summary?.yoYTurnover ?? []).map((e: any) => ({
            financialYear: e.financialYear ?? '',
            turnoverBand: e.turnoverBand ?? '',
            slabValue: Number(e.slabValue) || 0,
        })),
        stateCoverage,
        totalStates: summary?.stateCoverage?.totalStates ?? 0,
        signatories: Array.isArray(summary?.signatories) ? summary.signatories : [],
        rows: gstinRows,
    };
}

export function normalizeGstRevenue(
    gstr3bRaw: any,
    b2bRaw: any,
    gstOverviewRaw?: any,
    revenueChartsRaw?: any,
): GstRevenueProps | undefined {
    // GSTR-3B table — keep Revenue + Tax Liability (and any rows before the
    // first category header). Purchases / ITC Claimed from the same payload are
    // rendered by the GST Expenses module instead.
    const { data: gstr3bData, monthHeaders, rows: gstr3bRows } = parseGstr3b(gstr3bRaw, new Set(['', 'Revenue', 'Tax Liability']));
    // Raw header keys for the B2B customer-count lookup fallback below.
    const monthKeys3b = monthHeaders.map((h) => h.key);

    // B2B Customer table
    const b2bData = b2bRaw?.data?.data ?? b2bRaw?.data ?? {};
    const b2bRawHeaders = (b2bData?.headers ?? []).filter(
        (h: any) => h.key !== 'name' && !/state/i.test(String(h.key ?? '')) && !/state/i.test(String(h.name ?? '')),
    );
    // Cap B2B headers to last 12
    const b2bHeaders12 = b2bRawHeaders.slice(-12);
    const b2bMonthHeaders = b2bHeaders12.map((h: any) => ({ key: h.key, label: h.name ?? fmtMonthLabel(h.key) }));
    // B2B raw keys for data lookup
    const b2bKeys = b2bHeaders12.map((h: any) => h.key);
    const allB2bRows = b2bData?.rows ?? [];
    const b2bRows = allB2bRows.slice(0, 20).map((row: any) => {
        const amounts: Record<string, string> = {};
        for (let i = 0; i < b2bHeaders12.length; i++) {
            const h = b2bHeaders12[i];
            const displayLabel = b2bMonthHeaders[i]?.label ?? h.key;
            const val = row[h.key];
            amounts[displayLabel] = val != null ? String(val) : '—';
        }
        return { name: row.name ?? '—', state: row.state ?? '', bold: false, amounts };
    });

    // Use display labels in finalMonthHeaders
    const finalMonthHeaders =
        monthHeaders.length > 0
            ? monthHeaders.map((h) => ({ key: h.label, label: h.label }))
            : b2bMonthHeaders.map((h: any) => ({ key: h.label, label: h.label }));
    // B2B table headers must come from the B2B response itself — its row
    // amounts are keyed by these labels, which can differ from the GSTR-3B
    // month labels (mismatch made every cell render as "—").
    const finalB2bHeaders = b2bMonthHeaders.map((h: any) => ({ key: h.label, label: h.label }));
    if (gstr3bRows.length === 0 && b2bRows.length === 0) return undefined;

    // ════════════════════════════════════════════════════════════════
    //  CHART DATA — prefer server-computed graphs, fall back to derived
    // ════════════════════════════════════════════════════════════════
    const srvRevenue = getGraph(revenueChartsRaw, 'revenue');
    const srvGrowth = getGraph(revenueChartsRaw, 'revenueGrowth');
    const srvStates = getGraph(revenueChartsRaw, 'domesticRevenueAcrossStates');
    const srvRecurring = getGraph(revenueChartsRaw, 'splitOfExsistingCustomers');
    const srvAvgRev = getGraph(revenueChartsRaw, 'avgRevenuePerCustomer');
    const srvConc = getGraph(revenueChartsRaw, 'customerConcentration');
    const srvTop5 = getGraph(revenueChartsRaw, 'topFiveCustomers');
    const srvSegment = getGraph(revenueChartsRaw, 'revenuesBySegment');
    const srvType = getGraph(revenueChartsRaw, 'revenueType');
    const srvHsn = getGraph(revenueChartsRaw, 'revenueByHsnCategory');
    const srvExVsNew = getGraph(revenueChartsRaw, 'existingVsNewCustomer');
    const srvCohort = getGraph(revenueChartsRaw, 'customerCohort');

    const revenueTrend = extractLinePoints(srvRevenue);
    const revenueGrowth = extractLinePoints(srvGrowth);
    const stateBreakdown = extractBarItems(srvStates, 'rupees');
    const recurringCustomers = extractStackedBars(srvRecurring);
    const avgRevenuePerCustomer = extractLinePoints(srvAvgRev);
    const customerConcentration = extractConcentration(srvConc);
    const topCustomers = extractBarItems(srvTop5);
    const revenueBySegment = extractMultiLine(srvSegment);
    const revenueByType = extractMultiLine(srvType);
    const existingVsNewMulti = extractMultiLine(srvExVsNew);
    // The series order depends on legend naming — find "Existing"/"New" by label
    const exSeries = existingVsNewMulti.find((s) => /existing/i.test(s.label));
    const newSeries = existingVsNewMulti.find((s) => /new/i.test(s.label));
    const existingVsNew =
        existingVsNewMulti.length >= 2
            ? { existing: (exSeries ?? existingVsNewMulti[0]).points, new: (newSeries ?? existingVsNewMulti[1]).points }
            : undefined;
    const customerCohort = extractLinePoints(srvCohort);

    // B2B customer count for stats
    const b2bLookupKeys = b2bKeys.length > 0 ? b2bKeys : monthKeys3b.length > 0 ? monthKeys3b : [];
    const b2bParsed = allB2bRows
        .map((row: any) => {
            let total = 0;
            for (const k of b2bLookupKeys) {
                total += parseIndianNum(row[k]);
            }
            return { name: row.name ?? '', total };
        })
        .filter((r: any) => r.total > 0);
    const sortedCustomers = [...b2bParsed].sort((a, b) => b.total - a.total);

    // Per-chart headline text from server graph metadata (shared pickMeta).
    const chartMeta = {
        revenue: pickMeta(srvRevenue),
        revenueGrowth: pickMeta(srvGrowth),
        states: pickMeta(srvStates),
        recurring: pickMeta(srvRecurring),
        avgRevenue: pickMeta(srvAvgRev),
        concentration: pickMeta(srvConc),
        topCustomers: pickMeta(srvTop5),
        segment: pickMeta(srvSegment),
        revenueType: pickMeta(srvType),
        hsn: pickMeta(srvHsn),
        existingVsNew: pickMeta(srvExVsNew),
        cohort: pickMeta(srvCohort),
    };

    const metaStat = (g: any, fallbackLabel: string) => ({
        label: g?.metadata?.title ?? fallbackLabel,
        value: g?.metadata?.primaryText ?? '—',
        sub: g?.metadata?.secondaryText ?? '',
    });
    const growthStat = metaStat(srvGrowth, 'Growth (MoM)');
    const growthNum = parseFloat(String(growthStat.value).replace(/[^\d.-]/g, ''));
    const stats = [
        metaStat(srvRevenue, 'Revenue'),
        {
            ...growthStat,
            tone: Number.isFinite(growthNum) ? ((growthNum >= 0 ? 'green' : 'red') as 'green' | 'red') : undefined,
        },
        metaStat(srvAvgRev, 'Avg. Revenue Per Customer'),
        srvExVsNew?.metadata?.primaryText
            ? metaStat(srvExVsNew, 'New Business')
            : {
                  label: 'Total Customers (LTM)',
                  value: String(sortedCustomers.length || srvConc?.metadata?.totalCustomers || 0),
                  sub: 'B2B',
              },
    ];

    // ── HSN/SAC Categories (from GST Overview) ───────────────────
    const gstOverData = gstOverviewRaw?.data?.data ?? gstOverviewRaw?.data ?? gstOverviewRaw ?? {};
    const gstRows: any[] = gstOverData?.rows ?? [];
    // Collect all HSN descriptions across all GSTINs
    const hsnDescMap: Record<string, number> = {};
    for (const row of gstRows) {
        for (const h of row.hsn ?? []) {
            const desc = h.description || h.code || '';
            if (desc) hsnDescMap[desc] = (hsnDescMap[desc] || 0) + 1;
        }
        for (const s of row.sac ?? []) {
            const desc = s.description || s.code || '';
            if (desc) hsnDescMap[desc] = (hsnDescMap[desc] || 0) + 1;
        }
    }
    const hsnSorted = Object.entries(hsnDescMap).sort((a, b) => b[1] - a[1]);
    const hsnTotal = hsnSorted.length;

    // HSN chart: only render from the server-computed graph. The previous
    // GST-Overview fallback invented evenly-split percentages (not real
    // data), so when the server graph is absent the chart is hidden instead.
    const srvHsnSlices = extractHsnCategories(srvHsn);
    const finalHsnCategories = srvHsnSlices;
    // Product count: same as the dashboard card — metadata.totalProducts, or the
    // leading number in metadata.primaryText (e.g. "3 Product(s)"), then slice count.
    const metaProductCount = srvHsn?.metadata?.totalProducts ?? parseInt(String(srvHsn?.metadata?.primaryText ?? ''), 10);
    const finalHsnTotal = Number.isFinite(metaProductCount) && metaProductCount > 0 ? metaProductCount : srvHsnSlices?.length ?? hsnTotal;

    return {
        stats,
        chartMeta,
        monthHeaders: finalMonthHeaders,
        b2bMonthHeaders: finalB2bHeaders,
        gstr3bRows,
        b2bCustomers: b2bRows,
        gstin: gstr3bData?.gstin ?? undefined,
        // Chart data — sourced from server /insights/tab/data API
        revenueTrend: revenueTrend.length > 1 ? revenueTrend : undefined,
        revenueGrowth: revenueGrowth.length > 1 ? revenueGrowth : undefined,
        stateBreakdown: stateBreakdown.length > 0 ? stateBreakdown : undefined,
        topCustomers: topCustomers.length > 0 ? topCustomers : undefined,
        customerConcentration,
        avgRevenuePerCustomer: avgRevenuePerCustomer.length > 1 ? avgRevenuePerCustomer : undefined,
        recurringCustomers: recurringCustomers.length > 1 ? recurringCustomers : undefined,
        revenueBySegment: revenueBySegment.length > 0 ? revenueBySegment : undefined,
        revenueByType: revenueByType.length > 0 ? revenueByType : undefined,
        existingVsNew,
        customerCohort: customerCohort.length > 0 ? customerCohort : undefined,
        hsnCategories: finalHsnCategories,
        hsnTotalProducts: finalHsnTotal > 0 ? finalHsnTotal : undefined,
    };
}

export function normalizeGstExpenses(gstr3bRaw: any, b2bRaw: any, expenseChartsRaw?: any): GstExpenseProps | undefined {
    // Purchases & ITC table — parsed from the SAME GSTR-3B response the revenue
    // module uses, keeping only the Purchases / ITC Claimed categories.
    const { data: g3Data, monthHeaders: g3Months, rows: gstr2Rows } = parseGstr3b(gstr3bRaw, new Set(['Purchases', 'ITC Claimed']));
    // If only category headers were found (no actual data rows), hide the table.
    if (!gstr2Rows.some((r) => r.amounts)) gstr2Rows.length = 0;
    // Table headers use the same display labels the row amounts are keyed by.
    const gstr2MonthHeaders = g3Months.map((h) => ({ key: h.label, label: h.label }));

    // B2B Vendor table — self-contained: headers and amount keys both come from
    // the vendor-expense table response (display label used as lookup key).
    const b2bData = b2bRaw?.data?.data ?? b2bRaw?.data ?? {};
    const b2bRawHeaders = (b2bData?.headers ?? []).filter(
        (h: any) => h.key !== 'name' && !/state/i.test(String(h.key ?? '')) && !/state/i.test(String(h.name ?? '')),
    );
    // Cap to last 12 months, same as the revenue module
    const b2bHeaders12 = b2bRawHeaders.slice(-12);
    const b2bMonthHeaders = b2bHeaders12.map((h: any) => {
        const label = h.name ?? fmtMonthLabel(h.key);
        return { key: label, label };
    });
    const b2bRows = (b2bData?.rows ?? []).slice(0, 20).map((row: any) => {
        const amounts: Record<string, string> = {};
        for (let i = 0; i < b2bHeaders12.length; i++) {
            const h = b2bHeaders12[i];
            const val = row[h.key];
            amounts[b2bMonthHeaders[i].key] = val != null ? String(val) : '—';
        }
        return { name: row.name ?? '—', state: row.state ?? '', bold: false, amounts };
    });

    if (gstr2Rows.length === 0 && b2bRows.length === 0) return undefined;

    // ── Charts — server-computed vendor-expense graphs (same as dashboard) ──
    const srvExpVsRev = getGraph(expenseChartsRaw, 'vendorExpensesVsRevenue');
    const srvChange = getGraph(expenseChartsRaw, 'expensesChange');
    const srvConc = getGraph(expenseChartsRaw, 'vendorExpenseConcentration');
    const srvTopVendors = getGraph(expenseChartsRaw, 'topFiveVendors');
    const srvSplitVendors = getGraph(expenseChartsRaw, 'splitOfExsitingVendors');
    const srvExVsNew = getGraph(expenseChartsRaw, 'existingVsNewExpenses');

    // "Expenses" trend — the expenses series of the expenses-vs-revenue chart
    const expVsRevMulti = extractMultiLine(srvExpVsRev);
    const expSeries = expVsRevMulti.find((s) => /expense|purchase/i.test(s.label)) ?? expVsRevMulti[0];
    const expenseTrend = expSeries?.points ?? [];
    const expenseGrowth = extractLinePoints(srvChange);
    const topVendors = extractBarItems(srvTopVendors);
    const recurringVendors = extractStackedBars(srvSplitVendors);
    const exVsNewMulti = extractMultiLine(srvExVsNew);
    const exSeries = exVsNewMulti.find((s) => /existing/i.test(s.label));
    const newSeries = exVsNewMulti.find((s) => /new/i.test(s.label));
    const existingVsNewVendors =
        exVsNewMulti.length >= 2
            ? { existing: (exSeries ?? exVsNewMulti[0]).points, new: (newSeries ?? exVsNewMulti[1]).points }
            : undefined;
    const vendorConcentration = extractConcentration(srvConc);

    const chartMeta = {
        expenses: pickMeta(srvExpVsRev),
        expenseGrowth: pickMeta(srvChange),
        concentration: pickMeta(srvConc),
        topVendors: pickMeta(srvTopVendors),
        recurring: pickMeta(srvSplitVendors),
        existingVsNew: pickMeta(srvExVsNew),
    };

    return {
        stats: [],
        chartMeta,
        monthHeaders: b2bMonthHeaders,
        gstr2MonthHeaders,
        gstr2Rows,
        b2bVendors: b2bRows,
        gstin: g3Data?.gstin ?? undefined,
        expenseTrend: expenseTrend.length > 1 ? expenseTrend : undefined,
        expenseGrowth: expenseGrowth.length > 1 ? expenseGrowth : undefined,
        vendorConcentration,
        topVendors: topVendors.length > 0 ? topVendors : undefined,
        recurringVendors: recurringVendors.length > 1 ? recurringVendors : undefined,
        existingVsNewVendors,
    };
}

const toAggBankingProps = (entry: any, accountCount: number): AggBankingProps | undefined => {
    // First header is the classification column; the rest are month periods.
    // Sort chronologically, cap to the latest 12 months.
    // Label format mirrors the dashboard: "Oct_2023" → "Oct'23".
    const rawPeriodHeaders = (entry.headers ?? [])
        .filter((h: any) => h?.key !== 'classification' && h?.name !== 'classification')
        .map((h: any) => String(h.name ?? h.key ?? ''));
    const sortedPeriodHeaders = sortMonthHeadersChrono(rawPeriodHeaders);
    const headers = sortedPeriodHeaders.map((k: string) => ({ key: k, label: shortMonthLabel(k) })).slice(-12);
    if (headers.length === 0) return undefined;

    const BOLD_ROWS = new Set(['Missing Data', 'Opening Balance', 'Closing Balance']);
    const buildRow = (row: any) => {
        const isMissingData = row?.classification === 'Missing Data';
        const amountsMap: Record<string, string> = {};
        for (const a of row?.amounts ?? []) {
            if (a?.period != null) amountsMap[a.period] = a?.amountFormatted ?? '';
        }
        const values: Record<string, { text: string; negative?: boolean; special?: boolean }> = {};
        const label = row?.classification;
        for (const h of headers) {
            const val = amountsMap[h.key];
            if (val == null || val === '') {
                values[h.key] = { text: '—' };
            } else if (String(val).startsWith('-')) {
                values[h.key] = { text: `(${String(val).slice(1)})`, negative: true };
            } else {
                values[h.key] = { text: formatBounceCellText(label, String(val)), special: isMissingData };
            }
        }
        return { particular: row?.classification ?? '—', bold: BOLD_ROWS.has(row?.classification), values };
    };

    // Drop the "Missing Data" row from the rendered table.
    const { main, sections } = splitRowsBySection<any>(entry.rows ?? []);
    const mainRows = main.map(buildRow).filter((r) => r.particular !== 'Missing Data');
    const sectionRows = (header: string) => (sections.find((s) => s.header === header)?.rows ?? []).map(buildRow);

    if (mainRows.length === 0) return undefined;

    const toTable = (title: string, headerLabel: string, rows: any[]) =>
        rows.length > 0 ? { title, headerLabel, headers, rows } : undefined;

    return {
        stats: [],
        accountCount,
        mainTable: { title: '', headerLabel: 'Particulars', headers, rows: mainRows },
        bounceTable: toTable(BOUNCE_TRANSACTIONS_HEADER, 'Particulars', sectionRows(BOUNCE_TRANSACTIONS_HEADER)),
        otherTransactionFailuresTable: toTable(
            OTHER_TRANSACTION_FAILURES_HEADER,
            'Particulars',
            sectionRows(OTHER_TRANSACTION_FAILURES_HEADER),
        ),
        bounceChargesTable: toTable(BOUNCE_CHARGES_HEADER, 'Particulars', sectionRows(BOUNCE_CHARGES_HEADER)),
        cashChequeTable: toTable(CASH_AND_CHEQUE_HEADER, 'Particulars', sectionRows(CASH_AND_CHEQUE_HEADER)),
        creditsDebitsTable: toTable(CREDITS_AND_DEBITS_HEADER, 'Particulars', sectionRows(CREDITS_AND_DEBITS_HEADER)),
        paymentMethodTable: toTable(PAYMENT_METHOD_HEADER, 'Method', sectionRows(PAYMENT_METHOD_HEADER)),
    };
};

const isAggregatedEntry = (entry: any): boolean => String(entry?.bankName ?? '').toLowerCase() === AGGREGATED;

/**
 * Builds the consolidated view from the `Aggregated` entry of `bankData.summaryData`, falling back to the
 * first entry. Cells are looked up by the period header name (e.g. "Oct_2023").
 */
export function normalizeAggBanking(raw: any): AggBankingProps | undefined {
    const bankData = raw?.bankData ?? raw;
    const summaryData: any[] = Array.isArray(bankData?.summaryData) ? bankData.summaryData : [];
    const agg = summaryData.find(isAggregatedEntry) ?? summaryData[0];
    if (!agg) return undefined;
    const overview: any[] = Array.isArray(bankData?.bankingOverview) ? bankData.bankingOverview : [];
    return toAggBankingProps(agg, realBankingAccounts(overview).length);
}

export function normalizeBankAccountAnalyses(raw: any): BankAccountAnalysis[] | undefined {
    const bankData = raw?.bankData ?? raw;
    const summaryData: any[] = Array.isArray(bankData?.summaryData) ? bankData.summaryData : [];
    const overview: any[] = Array.isArray(bankData?.bankingOverview) ? realBankingAccounts(bankData.bankingOverview) : [];
    const seen = new Set<string>();
    const analyses = overview.flatMap((account) => {
        const accountNumber = account?.accountNumber;
        if (!accountNumber || seen.has(accountNumber)) return [];
        const entry = summaryData.find((s) => s?.accountNumber === accountNumber && !isAggregatedEntry(s));
        const body = entry ? toAggBankingProps(entry, 1) : undefined;
        if (!body) return [];
        seen.add(accountNumber);
        const bankName = account?.name ?? entry.bankName;
        return [{ bankName, accountNumber, label: bankAccountLabel(bankName, accountNumber), body }];
    });
    return analyses.length ? analyses : undefined;
}
